import { formatNumber } from "@/lib/format";
import { PageRow } from "../types/webstoreAnalytics";
import { Row, TablePanel, Td, Th } from "./AnalyticsTable";

interface TopPagesTableProps {
  pages: PageRow[];
  limit?: number;
}

/**
 * Page views per route.
 *
 * The paths here are normalised templates — `/order/[transactionId]`, not a real
 * transaction id. That collapsing happens upstream at ingest, both to keep
 * cardinality sane and to keep ids out of reporting. `/(other)` means a route the
 * gateway's route table has not caught up with yet.
 */
export function TopPagesTable({ pages, limit = 12 }: TopPagesTableProps) {
  const shown = pages.slice(0, limit);

  return (
    <TablePanel
      title="Top pages"
      subtitle="Normalised route templates, not raw URLs"
      isEmpty={shown.length === 0}
      empty="No page views in this range"
      footer={pages.length > limit ? `Showing top ${limit} of ${pages.length} routes.` : undefined}
      head={
        <>
          <Th>Route</Th>
          <Th align="right">Page views</Th>
          <Th align="right">Sessions</Th>
          <Th align="right">Visitors</Th>
          <Th align="right">Share</Th>
        </>
      }
    >
      {shown.map((page) => (
        <Row key={page.url_path}>
          <Td className="font-medium text-slate-800">
            <span className="num-mono text-[11px]">{page.url_path}</span>
            {page.url_path === "/(other)" && (
              <span className="ml-2 text-[10px] text-accent-amber">unmapped route</span>
            )}
          </Td>
          <Td align="right">{formatNumber(page.page_views)}</Td>
          <Td align="right">{formatNumber(page.sessions)}</Td>
          <Td align="right">{formatNumber(page.unique_visitors)}</Td>
          <Td align="right" className="text-right num-mono font-semibold text-slate-600">
            {page.share_pct}%
          </Td>
        </Row>
      ))}
    </TablePanel>
  );
}
